import {
  Fragment,
  useLayoutEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';

import { Icons } from '@/assets/icons/icons';
import { Command } from '@/components/ui/cmdk/cmdk';
import { Empty } from '@/components/ui/EmptyState';
import { YStack } from '@/components/ui/layout/layout';
import { Popover } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { isTouchPrimaryViewport } from '@/utils/touch-primary';

import { settingsMenuConfig } from './settings-menu.config';
import { openSettings, parseSettingsHash } from './settings-navigation';
import { recordSettingsSearchAcceptance } from './settings-search.acceptance';
import { registerPendingSettingsDeepLink } from './settings-search.registry';
import { useSettingsSearch } from './useSettingsSearch';

import type { SettingsSearchGroup } from './settings-search';

const EMPTY_STATE_DEBOUNCE_MS = 600;
const SETTINGS_SECTION_ICONS = new Map(
  settingsMenuConfig.map((section) => [
    section.hash.replace('settings/', ''),
    section.icon,
  ])
);

/**
 * Search input for the settings sidebar. Keeps `children` (the section menu)
 * visible and opens grouped, keyboard-navigable results in a wider popover.
 */
export const SettingsSidebarSearch = ({ children }: PropsWithChildren) => {
  const [query, setQuery] = useState('');
  const [selectedResult, setSelectedResult] = useState('');
  const { groups, providersLoading } = useSettingsSearch(query);
  const searching = query.trim().length > 0;

  const openResult = (hash: string) => {
    const target = parseSettingsHash(hash);
    if (target) void openSettings(target.section, target.item);
    setQuery('');
    setSelectedResult('');
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setQuery('');
      setSelectedResult('');
    }
  };

  return (
    <Command.Root
      shouldFilter={false}
      label="Search settings"
      className="flex-1 min-h-0"
      value={selectedResult}
      onValueChange={setSelectedResult}
      onKeyDown={(event) => {
        if (!event.metaKey && !event.ctrlKey) event.stopPropagation();
      }}
    >
      <Popover.Root open={searching} onOpenChange={handleOpenChange}>
        <Popover.Anchor asChild>
          <div className="rounded-lg border border-border-strong bg-surface shadow-sm focus-within:border-accent [&_[cmdk-input-wrapper]]:mb-0 [&_[cmdk-input-wrapper]]:border-0 [&_[cmdk-input-wrapper]]:px-[11px] [&_[cmdk-input-wrapper]_svg]:size-3.5">
            <Command.Input
              autoFocus={!isTouchPrimaryViewport()}
              value={query}
              onValueChange={setQuery}
              placeholder="Search"
              className="h-8 py-1"
            />
          </div>
        </Popover.Anchor>
        <Popover.Content
          align="start"
          side="bottom"
          sideOffset={6}
          collisionPadding={12}
          className="w-[28rem] max-w-[calc(100vw-2rem)] p-1 !animate-none !transition-none"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          <SearchResults
            query={query}
            groups={groups}
            providersLoading={providersLoading}
            selectedValue={selectedResult}
            onSelect={openResult}
            onFirstResultChange={setSelectedResult}
          />
        </Popover.Content>
      </Popover.Root>
      {children}
    </Command.Root>
  );
};

const SearchResults = ({
  query,
  groups,
  providersLoading,
  selectedValue,
  onSelect,
  onFirstResultChange,
}: {
  query: string;
  groups: SettingsSearchGroup[];
  /** Hold back the empty state until every provider has settled */
  providersLoading: boolean;
  selectedValue: string;
  onSelect: (hash: string) => void;
  onFirstResultChange: (value: string) => void;
}) => {
  const normalizedQuery = query.trim();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [settledEmptyQuery, setSettledEmptyQuery] = useState<string | null>(
    null
  );
  const [lastNonEmptyResults, setLastNonEmptyResults] = useState<{
    groups: SettingsSearchGroup[];
    query: string;
  } | null>(null);

  useLayoutEffect(() => {
    if (groups.length > 0) {
      setLastNonEmptyResults({ groups, query });
    }
  }, [groups, query]);

  useLayoutEffect(() => {
    if (!normalizedQuery || groups.length > 0 || providersLoading) {
      setSettledEmptyQuery(null);
      return;
    }

    const timeoutId = window.setTimeout(
      () => setSettledEmptyQuery(normalizedQuery),
      EMPTY_STATE_DEBOUNCE_MS
    );
    return () => window.clearTimeout(timeoutId);
  }, [groups.length, normalizedQuery, providersLoading]);

  const showEmptyState =
    groups.length === 0 &&
    !providersLoading &&
    settledEmptyQuery === normalizedQuery;
  const retainedResults =
    normalizedQuery && groups.length === 0 && !showEmptyState
      ? lastNonEmptyResults
      : null;
  const displayedGroups = retainedResults?.groups ?? groups;
  // fills the window where the panel has nothing else to render: providers
  // still resolving on the first keystrokes, or the empty-state debounce
  const showLoadingState =
    normalizedQuery.length > 0 &&
    displayedGroups.length === 0 &&
    !showEmptyState;
  const displayedQuery = retainedResults?.query ?? query;
  const displayedResultsKey = JSON.stringify(
    displayedGroups.map((group) => [
      group.sectionId,
      group.sectionMatched,
      group.entries.map((entry) => entry.id),
    ])
  );
  const firstResultValue = getFirstResultValue(displayedGroups);

  useLayoutEffect(() => {
    onFirstResultChange(firstResultValue);
    queueMicrotask(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
    });
  }, [displayedResultsKey, firstResultValue, onFirstResultChange]);

  useLayoutEffect(() => {
    // cmdk's deferred scroll trails its controlled selection by one row inside
    // SimpleBar, so keep the selected value within the actual scroll viewport.
    const container = scrollContainerRef.current;
    const selectedItem = Array.from(
      container?.querySelectorAll<HTMLElement>('[cmdk-item]') ?? []
    ).find((item) => item.dataset.value === selectedValue);
    if (!container || !selectedItem) return;

    const containerBounds = container.getBoundingClientRect();
    const itemBounds = selectedItem.getBoundingClientRect();
    if (itemBounds.top < containerBounds.top) {
      container.scrollTop += itemBounds.top - containerBounds.top;
    } else if (itemBounds.bottom > containerBounds.bottom) {
      container.scrollTop += itemBounds.bottom - containerBounds.bottom;
    }
  }, [selectedValue]);

  return (
    <Command.List
      containerRef={scrollContainerRef}
      aria-busy={providersLoading}
      containerClassName="min-h-40 max-h-[min(28rem,var(--radix-popover-content-available-height))]"
      className="p-1"
    >
      {showLoadingState && <SettingsSearchLoadingState />}
      {showEmptyState && <SettingsSearchEmptyState query={query} />}
      {displayedGroups.map((group) => {
        const SectionIcon = SETTINGS_SECTION_ICONS.get(group.sectionId);
        return (
          <Command.Group
            key={group.sectionId}
            heading={
              <span className="flex items-center gap-1.5">
                {SectionIcon && (
                  <SectionIcon size={12} className="text-foreground-muted" />
                )}
                <span>{group.sectionTitle}</span>
              </span>
            }
          >
            {group.sectionMatched && (
              <Command.Item
                value={`section:${group.sectionId}`}
                aria-label={group.sectionTitle}
                className="bg-transparent"
                onSelect={() => onSelect(`settings/${group.sectionId}`)}
              >
                <span className="text-sm">
                  <HighlightedText
                    text={group.sectionTitle}
                    query={displayedQuery}
                  />
                </span>
              </Command.Item>
            )}
            {/* the entry id rides along in the hash so PR 3 can deep-link to the row */}
            {group.entries.map((entry) => (
              <Command.Item
                key={entry.id}
                value={entry.id}
                aria-label={entry.label}
                className="bg-transparent"
                onSelect={() => {
                  // remembered so similar queries rank this entry first on
                  // ties; displayedQuery is the query these results are for
                  recordSettingsSearchAcceptance(displayedQuery, entry.id);
                  registerPendingSettingsDeepLink(entry);
                  onSelect(`settings/${group.sectionId}/${entry.id}`);
                }}
              >
                <YStack className="min-w-0 w-full gap-0.5">
                  <span className="text-sm">
                    <HighlightedText
                      text={entry.label}
                      query={displayedQuery}
                    />
                  </span>
                  {entry.description && (
                    <span className="text-xs text-foreground-muted">
                      <HighlightedText
                        text={entry.description}
                        query={displayedQuery}
                      />
                    </span>
                  )}
                  {getMatchingKeyword(entry, displayedQuery) && (
                    <span className="text-xs text-foreground-muted">
                      Matches{' '}
                      <HighlightedText
                        text={getMatchingKeyword(entry, displayedQuery) ?? ''}
                        query={displayedQuery}
                      />
                    </span>
                  )}
                </YStack>
              </Command.Item>
            ))}
          </Command.Group>
        );
      })}
    </Command.List>
  );
};

const getFirstResultValue = (groups: SettingsSearchGroup[]): string => {
  const firstGroup = groups[0];
  if (!firstGroup) return '';
  if (firstGroup.sectionMatched) return `section:${firstGroup.sectionId}`;
  return firstGroup.entries[0]?.id ?? '';
};

const SettingsSearchLoadingState = () => (
  <YStack
    role="status"
    aria-label="Loading results"
    className="min-h-[19.125rem] gap-3 px-2 py-2"
  >
    <Skeleton className="h-3 w-1/3" />
    <Skeleton className="h-10 w-full" />
  </YStack>
);

const SettingsSearchEmptyState = ({ query }: { query: string }) => {
  const trimmedQuery = query.trim();
  const displayQuery =
    trimmedQuery.length > 40
      ? `${trimmedQuery.slice(0, 20)}…${trimmedQuery.slice(-19)}`
      : trimmedQuery;

  return (
    <Empty.Root variant="plain">
      <Empty.Media variant="icon">
        <Icons.search />
      </Empty.Media>
      <Empty.Header>
        <Empty.Title>No Results for &ldquo;{displayQuery}&rdquo;</Empty.Title>
        <Empty.Description>
          Check the spelling or try a new search.
        </Empty.Description>
      </Empty.Header>
    </Empty.Root>
  );
};

const getMatchingKeyword = (
  entry: SettingsSearchGroup['entries'][number],
  query: string
): string | undefined => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return undefined;

  const visibleText = `${entry.label} ${entry.description ?? ''}`.toLowerCase();
  if (visibleText.includes(normalizedQuery)) return undefined;

  return entry.keywords?.find((keyword) =>
    keyword.toLowerCase().includes(normalizedQuery)
  );
};

const HighlightedText = ({ text, query }: { text: string; query: string }) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return text;

  const normalizedText = text.toLowerCase();
  const parts = [];
  let cursor = 0;
  let matchIndex = normalizedText.indexOf(normalizedQuery);

  while (matchIndex !== -1) {
    if (matchIndex > cursor) {
      parts.push(
        <span
          key={`text-${cursor}-${matchIndex}`}
          className="text-foreground opacity-75"
        >
          {text.slice(cursor, matchIndex)}
        </span>
      );
    }

    parts.push(
      <mark
        key={`match-${matchIndex}`}
        className="bg-transparent font-normal text-foreground"
      >
        {text.slice(matchIndex, matchIndex + normalizedQuery.length)}
      </mark>
    );
    cursor = matchIndex + normalizedQuery.length;
    matchIndex = normalizedText.indexOf(normalizedQuery, cursor);
  }

  if (cursor < text.length) {
    parts.push(
      <span
        key={`text-${cursor}-${text.length}`}
        className="text-foreground opacity-75"
      >
        {text.slice(cursor)}
      </span>
    );
  }
  return <Fragment>{parts}</Fragment>;
};
